import React, { useState } from "react";
import { Check, X } from "lucide-react";
import { TOK, SERIF, SANS, PrimaryBtn, QuietLink, Title } from "./shared";

const PREVIEW = 8;

export const EditList = ({ sections, setSections, extended = false }) => {
  const move = (si, li, d) => setSections(sections.map((s, i) => {
    if (i !== si || li + d < 0 || li + d >= s.lines.length) return s;
    const lines = [...s.lines]; [lines[li], lines[li + d]] = [lines[li + d], lines[li]];
    return { ...s, lines };
  }));
  const add = () => setSections(sections.map((s, i) => (i !== sections.length - 1 ? s : { ...s, lines: [...s.lines, { text: "" }] })));
  const update = (si, li, text) => setSections(sections.map((s, i) => (i !== si ? s : { ...s, lines: s.lines.map((l, j) => (j === li ? { ...l, text } : l)) })));
  const remove = (si, li) => setSections(sections.map((s, i) => (i !== si ? s : { ...s, lines: s.lines.filter((_, j) => j !== li) })));
  return (
    <div className="border-t" style={{ borderColor: "rgba(215,187,122,0.16)" }} data-testid="simple-words-edit">
      {sections.map((s, si) => s.lines.map((l, li) => (
        <div key={`${si}-${li}`} className="flex items-start gap-2 py-2 border-b" style={{ borderColor: "rgba(215,187,122,0.16)" }}>
          <textarea value={l.text} rows={2} onChange={(e) => update(si, li, e.target.value)} data-testid={`simple-words-edit-${si}-${li}`}
            className="flex-1 bg-transparent py-1.5 text-lg leading-snug outline-none resize-none focus:bg-[rgba(245,236,217,0.04)] transition-colors"
            style={{ color: TOK.cream, fontFamily: SERIF, caretColor: TOK.gold }} />
          {extended && (
            <span className="mt-1 flex flex-col">
              <button type="button" onClick={() => move(si, li, -1)} aria-label="Move up" data-testid={`simple-words-up-${si}-${li}`} className="px-1 text-xs" style={{ color: TOK.creamMist }}>▲</button>
              <button type="button" onClick={() => move(si, li, 1)} aria-label="Move down" data-testid={`simple-words-down-${si}-${li}`} className="px-1 text-xs" style={{ color: TOK.creamMist }}>▼</button>
            </span>
          )}
          <button type="button" onClick={() => remove(si, li)} aria-label="Remove line" data-testid={`simple-words-remove-${si}-${li}`}
            className="mt-1.5 w-7 h-7 inline-flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity" style={{ color: TOK.creamDim }}>
            <X size={14} />
          </button>
        </div>
      )))}
      {extended && <button type="button" onClick={add} data-testid="simple-words-add" className="mt-3 text-[0.62rem] uppercase tracking-[0.22em]" style={{ color: TOK.goldSoft, fontFamily: SANS }}>+ Add a line</button>}
    </div>
  );
};

const Themes = ({ items }) => (
  <div className="flex flex-wrap gap-2" data-testid="simple-agree-themes">
    {items.map((t) => (
      <span key={t} className="rounded-full px-4 py-2 text-sm" style={{ border: `1px solid ${TOK.gold}`, color: TOK.cream, fontFamily: SANS, background: "rgba(215,187,122,0.08)" }}>{t}</span>
    ))}
  </div>
);

// Step 2 — Agree. Soulumo Coach proposes the direction and the words; the user agrees or changes something.
export const WordsStep = ({ plan, sections, setSections, onApprove, onReExplain, approveLabel = null, hideReExplain = false }) => {
  const [editing, setEditing] = useState(false);
  const [all, setAll] = useState(false);
  const lines = sections.flatMap((s) => s.lines.map((l) => l.text)).filter(Boolean);
  const shown = all ? lines : lines.slice(0, PREVIEW);
  const coach = plan.coach_line || plan.interpretation?.desired_state;
  return (
    <div className="space-y-7" data-testid="simple-words-step">
      <div>
        <div className="text-[0.62rem] uppercase tracking-[0.3em]" style={{ color: TOK.goldSoft, fontFamily: SANS }}>Soulumo Coach</div>
        <div className="mt-2"><Title>{plan.blueprint_id ? "Your words, ready for your voice." : "I understand."}</Title></div>
        {coach && <p className="mt-3 text-xl italic" style={{ color: TOK.creamDim, fontFamily: SERIF }} data-testid="simple-agree-coach-line">{coach}</p>}
      </div>
      {!!plan.focus_themes?.length && <Themes items={plan.focus_themes} />}
      <div>
        <div className="text-[0.62rem] uppercase tracking-[0.3em] mb-3" style={{ color: TOK.goldSoft, fontFamily: SANS }}>Your words</div>
        {editing ? <EditList sections={sections} setSections={setSections} /> : (
          <ul className="border-t" style={{ borderColor: "rgba(215,187,122,0.16)" }} data-testid="simple-words-list">
            {shown.map((t, i) => (
              <li key={i} className="flex items-baseline gap-4 py-3.5 border-b" style={{ borderColor: "rgba(215,187,122,0.16)" }}>
                <span className="shrink-0 w-5 text-right text-[0.62rem] tabular-nums" style={{ color: TOK.goldSoft, fontFamily: SANS, letterSpacing: "0.1em" }}>{String(i + 1).padStart(2, "0")}</span>
                <span className="text-xl leading-snug" style={{ color: TOK.cream, fontFamily: SERIF }}>{t}</span>
              </li>
            ))}
          </ul>
        )}
        {!editing && lines.length > PREVIEW && (
          <div className="mt-3"><QuietLink testId="simple-words-show-all" onClick={() => setAll((v) => !v)}>{all ? "Show fewer" : `Show all ${lines.length}`}</QuietLink></div>
        )}
      </div>
      <PrimaryBtn testId="simple-words-approve" disabled={!lines.length} onClick={onApprove}>
        <Check size={16} /> {editing ? "Done" : approveLabel || "Looks good"}
      </PrimaryBtn>
      {!editing && (
        <div className="flex items-center justify-center gap-6">
          <QuietLink testId="simple-words-adjust" onClick={() => setEditing(true)}>Change something</QuietLink>
          {!hideReExplain && <QuietLink testId="simple-words-reexplain" onClick={onReExplain}>Explain again</QuietLink>}
        </div>
      )}
    </div>
  );
};
